import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Badge, Button, Spinner, Textarea } from '../../components/UI';
import { APPOINTMENT_STATUS_LABELS, bookingApi, IPublicBooking, isCrmError } from '../../services/crm';
import { browserTimeZone, dayIn, timeIn } from '../sales/shared/calendar.helpers';
import { BookingShell, InlineError } from './components/BookingShell.component';
import { SlotPicker } from './components/SlotPicker.component';
import { WhenWhere } from './components/WhenWhere.component';

type Mode = 'view' | 'reschedule' | 'cancel';

// #region BookingManage
/**
 * /book/manage/:token — an invitee's own booking. The token in the link is
 * the only credential, so this shows one booking and nothing about the rep's
 * other time.
 */
const BookingManage: React.FunctionComponent<{}> = (): React.ReactElement => {
    const { token = '' } = useParams<{ token: string }>();
    // Email buttons land here with ?do=reschedule or ?do=cancel.
    const [params] = useSearchParams();
    const asked = params.get('do');
    const [booking, setBooking] = React.useState<IPublicBooking | null>(null);
    const [missing, setMissing] = React.useState<boolean>(false);
    const [mode, setMode] = React.useState<Mode>(asked === 'reschedule' || asked === 'cancel' ? asked : 'view');
    const [timeZone, setTimeZone] = React.useState<string>(browserTimeZone());
    const [pending, setPending] = React.useState<string | null>(null);   // a picked new start, awaiting confirm
    const [reason, setReason] = React.useState<string>('');
    const [busy, setBusy] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [refreshKey, setRefreshKey] = React.useState<number>(0);
    const [notice, setNotice] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = 'Wallmates | Your booking';
        bookingApi.manage(token).then((result) => {
            if (isCrmError(result)) { setMissing(true); return; }
            setBooking(result);
            if (result.invitee_time_zone) setTimeZone(result.invitee_time_zone);
        });
    }, [token]);

    const reschedule = async () => {
        if (!pending) return;
        setBusy(true);
        setError(null);
        const result = await bookingApi.reschedule(token, pending);
        setBusy(false);
        if (isCrmError(result)) {
            if (result.status === 409) { setPending(null); setRefreshKey((k) => k + 1); }
            setError(result.message);
            return;
        }
        setBooking(result);
        setMode('view');
        setPending(null);
        setNotice('Moved. An updated invitation is on its way.');
    };

    const confirm = async () => {
        setBusy(true);
        setError(null);
        const result = await bookingApi.confirm(token);
        setBusy(false);
        if (isCrmError(result)) { setError(result.message); return; }
        setBooking(result);
        setNotice(`Confirmed. ${result.rep_name} will see you then.`);
    };

    const cancel = async () => {
        setBusy(true);
        setError(null);
        const result = await bookingApi.cancel(token, reason.trim() || null);
        setBusy(false);
        if (isCrmError(result)) { setError(result.message); return; }
        setBooking(result);
        setMode('view');
        setNotice(null);
    };

    if (missing) {
        return (
            <BookingShell>
                <div className="px-6 py-16 text-center">
                    <h1 className="text-lg font-semibold">We couldn&apos;t find that booking</h1>
                    <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">The link may be incomplete. Use the one from your confirmation email.</p>
                </div>
            </BookingShell>
        );
    }
    if (!booking) return <BookingShell><div className="flex justify-center px-6 py-16"><Spinner show={true} /></div></BookingShell>;

    const scheduled = booking.status === 'scheduled';
    const tone = scheduled ? 'emerald' : booking.status === 'cancelled' ? 'red' : 'zinc';
    const statusLabel = scheduled && booking.confirmed_at ? 'Confirmed' : APPOINTMENT_STATUS_LABELS[booking.status];
    const deadline = booking.confirm_by ? `${dayIn(booking.confirm_by, timeZone)} at ${timeIn(booking.confirm_by, timeZone)}` : null;

    return (
        <BookingShell>
            <div className="mx-auto max-w-2xl px-5 py-8 sm:px-8 sm:py-10">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h1 className="text-xl font-semibold">Your booking with {booking.rep_name}</h1>
                    <Badge color={tone}>{statusLabel}</Badge>
                </div>
                {notice ? <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-400">{notice}</p> : null}

                <div className="mt-5 rounded-xl border border-surface-border bg-surface p-4">
                    {booking.slug ? (
                        <div className="mb-3 text-xs text-zinc-500 dark:text-zinc-400">{booking.invitee_name} · {booking.invitee_email}</div>
                    ) : null}
                    <WhenWhere title={booking.title} startIso={booking.start_at} endIso={booking.end_at} timeZone={timeZone} meetLink={booking.meet_link} />
                </div>

                {!scheduled ? (
                    <div className="mt-6 text-sm text-zinc-600 dark:text-zinc-400">
                        {booking.status === 'cancelled' ? 'This meeting was cancelled.' : 'This meeting has already happened.'}{' '}
                        {booking.slug ? <Link to={`/book/${booking.slug}`} className="font-medium text-emerald-700 underline underline-offset-2 dark:text-emerald-400">Book a new time</Link> : null}
                    </div>
                ) : null}

                {scheduled && mode === 'view' && booking.needs_confirmation ? (
                    <div className="mt-5 rounded-xl border border-emerald-600/40 bg-emerald-600/5 p-4">
                        <h2 className="text-sm font-semibold">Can you still make it?</h2>
                        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                            {deadline ? `Please confirm by ${deadline}` : 'Please confirm'}
                            {booking.releases_if_unconfirmed ? ' to keep your spot.' : ` so ${booking.rep_name} knows you're coming.`}
                        </p>
                        <InlineError message={error} />
                        <div className="mt-3"><Button color="emerald" disabled={busy} onClick={confirm}>{busy ? 'Confirming…' : 'Confirm my meeting'}</Button></div>
                    </div>
                ) : null}

                {scheduled && mode === 'view' ? (
                    <div className="mt-6 flex flex-wrap gap-2">
                        <Button outline onClick={() => { setMode('reschedule'); setError(null); setNotice(null); }}>Reschedule</Button>
                        <Button plain onClick={() => { setMode('cancel'); setError(null); setNotice(null); }}><span className="text-red-600">Cancel booking</span></Button>
                        <a href={bookingApi.icsUrl(token)} className="self-center px-2 text-sm font-medium text-emerald-700 underline underline-offset-2 dark:text-emerald-400">Add to calendar (.ics)</a>
                    </div>
                ) : null}

                {scheduled && mode === 'reschedule' ? (
                    <div className="mt-6">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-sm font-semibold">Pick a new time</h2>
                            <button type="button" onClick={() => { setMode('view'); setPending(null); }} className="text-xs text-zinc-500 underline underline-offset-2">Keep the current time</button>
                        </div>
                        <InlineError message={error} />
                        {pending ? (
                            <div className="mt-3 rounded-xl border border-emerald-600/40 bg-emerald-600/5 p-4">
                                <WhenWhere title={booking.title} startIso={pending} endIso={new Date(Date.parse(pending) + (Date.parse(booking.end_at) - Date.parse(booking.start_at))).toISOString()} timeZone={timeZone} />
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <Button color="emerald" disabled={busy} onClick={reschedule}>{busy ? 'Moving…' : 'Confirm new time'}</Button>
                                    <Button outline disabled={busy} onClick={() => setPending(null)}>Pick another</Button>
                                </div>
                            </div>
                        ) : booking.slug && booking.meeting_type_id ? (
                            <div className="mt-3">
                                <SlotPicker slug={booking.slug} typeId={booking.meeting_type_id} timeZone={timeZone} onTimeZone={setTimeZone} onPick={setPending} refreshKey={refreshKey} />
                            </div>
                        ) : (
                            <p className="text-sm text-zinc-500">This meeting type is no longer offered online — reply to your confirmation email to move it.</p>
                        )}
                    </div>
                ) : null}

                {scheduled && mode === 'cancel' ? (
                    <div className="mt-6 rounded-xl border border-red-200 bg-red-50/50 p-4 dark:border-red-900 dark:bg-red-500/5">
                        <h2 className="text-sm font-semibold">Cancel this meeting?</h2>
                        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{booking.rep_name} will be told. You can book again any time.</p>
                        <Textarea rows={2} className="mt-3" value={reason} placeholder="Reason (optional)" onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) => setReason(ev.target.value)} />
                        <InlineError message={error} />
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Button color="red" disabled={busy} onClick={cancel}>{busy ? 'Cancelling…' : 'Yes, cancel it'}</Button>
                            <Button outline disabled={busy} onClick={() => setMode('view')}>Keep it</Button>
                        </div>
                    </div>
                ) : null}
            </div>
        </BookingShell>
    );
};

export default BookingManage;
